//eslint-disable-next-line no-restricted-imports
import type Vue from "vue";
//eslint-disable-next-line no-restricted-imports
import type { PluginFunction } from "vue";
import type { Language } from "@utils/type/type";
import type { RoleResource, RoleResourceConstraint } from "@newgenerated/shared/schema";
import { getResourceConstraints } from "@generated/api/roleResourceConstraintControllerApi";
import { delay } from "@utils/asyncUtils";
import type { HasAccessFn } from "@utils/widgetContextUtils";

function hasLanguage(constraint: RoleResourceConstraint, language?: Language): boolean {
  if (language === undefined || constraint.languages.length === 0) {
    return true;
  }
  return constraint.languages.includes(language);
}

function hasCorporation(constraint: RoleResourceConstraint, corporationId: number | undefined): boolean {
  if (corporationId === undefined || constraint.corporateIds.length === 0) {
    return true;
  }
  return constraint.corporateIds.includes(corporationId);
}

type RoleResourceConstraintState =
  | {
      type: "MISSING" | "LOADING";
    }
  | {
      type: "AVAILABLE";
      constraints: RoleResourceConstraint[];
    };

let state: RoleResourceConstraintState = { type: "MISSING" };

export async function setupRoleResourceConstraints(): Promise<RoleResourceConstraint[]> {
  while (true) {
    if (state.type === "MISSING") {
      // Load role resource constraints for the first time
      state = { type: "LOADING" };
      const constraints = await getResourceConstraints();
      state = { type: "AVAILABLE", constraints };
      return constraints;
    }

    if (state.type === "AVAILABLE") {
      // Role resource constraints have already been set up
      return state.constraints;
    }

    // There is a request for the role resource constraints in process
    await delay(100);
  }
}

export async function hasAccess(resource: RoleResource, language?: Language, corporateId?: number): Promise<boolean> {
  const constraints = await setupRoleResourceConstraints();
  const constraint = constraints.find((roleResourceConstraint) => roleResourceConstraint.resource === resource);
  return constraint !== undefined && hasLanguage(constraint, language) && hasCorporation(constraint, corporateId);
}

export async function getCustomerIdFromConstraints(resource: RoleResource): Promise<number | null> {
  const constraints = await setupRoleResourceConstraints();
  const constraint = constraints.find((roleResourceConstraint) => roleResourceConstraint.resource === resource);
  return constraint?.customerId ?? null;
}

/*
 * Sync RoleResource handling
 * Should probably be deprecated soon, as it is possible to call the functions in the wrong order.
 */

const fetchedConstraints: RoleResourceConstraint[] = [];

export async function fetchRoleResources(): Promise<void> {
  const instances = await getResourceConstraints();
  fetchedConstraints.push(...instances);
}

/**
 * This prerequisites <code>fetchRoleResources()</code> to have been run before. Therefore it is safer to use <code>hasAccess</code> instead, as it makes sure that the role resource constraints have been fetched.
 */
export const hasAccessSync = (resource: RoleResource, language?: Language, corporateId?: number): boolean => {
  const constraint = fetchedConstraints.find((roleResourceConstraint) => roleResourceConstraint.resource === resource);
  return constraint !== undefined && hasLanguage(constraint, language) && hasCorporation(constraint, corporateId);
};

export const createHasAccessFunction = (fetchedConstraints: RoleResourceConstraint[]): HasAccessFn => {
  return (resource: RoleResource, language?: Language, corporateId?: number) => {
    const constraint = fetchedConstraints.find((roleResourceConstraint) => roleResourceConstraint.resource === resource);
    return constraint !== undefined && hasLanguage(constraint, language) && hasCorporation(constraint, corporateId);
  };
};

/**
 * This prerequisites <code>fetchRoleResources()</code> to have been run before. Therefore it is safer to use <code>getCustomerIdFromConstraints</code> instead, as it makes sure that the role resource constraints have been fetched.
 */
export const getCustomerIdFromConstraintsSync = (resource: RoleResource): number | null => {
  const constraint = fetchedConstraints.find((roleResourceConstraint) => roleResourceConstraint.resource === resource);
  return constraint?.customerId ?? null;
};

export class RoleService {
  constructor() {}

  static install: PluginFunction<never> = (vue: typeof Vue): void => {
    vue.prototype.$access = hasAccessSync;

    vue.prototype.$accessibleLanguagesOf = (resource: RoleResource, corporateId?: number): Language[] => {
      const constraint = fetchedConstraints.find((roleResourceConstraint) => roleResourceConstraint.resource === resource);

      if (constraint !== undefined && hasCorporation(constraint, corporateId)) {
        return constraint.languages;
      }
      return [];
    };
  };
}

declare module "vue/types/vue" {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Vue {
    $access: (resource: RoleResource, language?: Language, corporateId?: number) => boolean;
    $accessibleLanguagesOf: (resource: RoleResource, corporateId?: number) => number[];
  }
}
